/** #@condition: vision, cds **/
/** Only for vision **/

import {bootstrapWorker} from 'pt-vision/bootstrap/worker';
import {VisionContentTransporter} from 'pt-vision/content-transporter';

import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

import {Logger} from '#core/utils/logger.js';
import {resolveContentLocale} from '#core/utils/locale-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {isCrawlerBot} from '#core/utils/url-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {buildVisionConfig} from '#core/config/vision/index.js';
import {resolveCurrentLanguage} from '#core/localization.js';

const log = Logger('VisionInitializer');
const DEBUG_GROUP = 'VisionInitializer';

const {dispatch} = MojitoCore.Services.redux.store;
const {prefetchDataSuccess} = MojitoServices.Prefetch.actions;

const SYSTEM_SETTINGS_STORAGE_KEY = 'systemSettings';
const ADDITIONAL_CONTEXT_PARAM = 'additionalContext';
const CHANNEL = {
    MOBILE: 'mobile',
    NATIVE: 'native',
    DESKTOP: 'desktop',
};

function getAdditionalContextFromStorage() {
    try {
        return JSON.parse(localStorage.getItem(SYSTEM_SETTINGS_STORAGE_KEY))?.additionalContext;
    } catch {
        return undefined;
    }
}

function getAdditionalContextFromUrl() {
    return new URLSearchParams(window.location.search).get(ADDITIONAL_CONTEXT_PARAM) || undefined;
}

function detectChannel() {
    const additionalContext = getAdditionalContextFromUrl() || getAdditionalContextFromStorage();
    if (additionalContext === CHANNEL.NATIVE) {
        return CHANNEL.NATIVE;
    }

    return navigator.userAgent?.includes('Mobi') ? CHANNEL.MOBILE : CHANNEL.DESKTOP;
}

function isServiceWorkerDisabled() {
    return new URLSearchParams(window.location.search).get('noSW') === '1' || isCrawlerBot();
}

function isServiceWorkerAvailable() {
    return !isServiceWorkerDisabled() && 'serviceWorker' in navigator;
}

// Feed connection params passed to the service worker via its registration URL query string
// (and to the inline bootstrap as a fallback). Evaluated inside the worker by pt-vision/sw.
function getFeedParams({contentUrl, broadcastUrl, wasmFileUrl, websocketUrl}) {
    const broadcastUrls = [];
    const broadcastTransports = [];

    // Priority channel: CDN WebSocket endpoint (when available)
    if (websocketUrl) {
        broadcastUrls.push(websocketUrl);
        broadcastTransports.push('websocket');
    }
    // Fallback channel: mojito internal content delivery endpoint over SocketIO
    broadcastUrls.push(`${broadcastUrl}/vws`);
    broadcastTransports.push('socketio');

    return {
        contentUrl,
        broadcastUrl: broadcastUrls.join('|'),
        broadcastTransport: broadcastTransports.join('|'),
        channel: detectChannel(),
        wasmFileUrl,
    };
}

function getServiceWorkerConfig(feedParams) {
    const useServiceWorker = isServiceWorkerAvailable() && USE_SERVICE_WORKER;
    if (!useServiceWorker) {
        return undefined;
    }

    const suffix = process.env.NODE_ENV !== 'development' ? '.min' : '';

    const scriptURL = new URL(
        window.SPORTS_SW_LIBRARY ?? `/service-worker-${VISION_VERSION}-${GIT_HASH}${suffix}.js`,
        window.location.origin
    );

    const query = new URLSearchParams(scriptURL.search);
    Object.entries(feedParams).forEach(([key, value]) => {
        if (value === undefined) {
            return;
        }

        query.set(key, value);
    });
    scriptURL.search = query.toString();

    return {
        scriptURL: scriptURL.href,
        options: {
            scope: '/',
        },
    };
}

const bootstrapVisionInline = async feedParams => {
    const {bootstrapInline} = await import(
        /* webpackChunkName: "vision-inline-bootstrap" */ 'pt-vision/bootstrap/inline'
    );

    const {sportsContent = {}, search = {}} = ApplicationConfig.config;
    const visionConfig = buildVisionConfig(sportsContent, search);

    bootstrapInline(visionConfig, feedParams);
};

const bootstrapVisionWorker = async (swConfig, extraParams) => {
    if (!swConfig) {
        throw new Error('Vision service worker config is not available');
    }

    log.info(`%cInitialize Vision in worker mode`, Logger.STYLE_DEBUG, swConfig, extraParams);

    await bootstrapWorker(swConfig, extraParams);
};

export async function getVisionContentTransporter({subscriptionResolver}) {
    const onDataPreload = preloadedData => {
        dispatch(prefetchDataSuccess(preloadedData));
    };

    return new VisionContentTransporter(subscriptionResolver, onDataPreload, DEBUG_GROUP);
}

export async function bootstrapVisionTransport({broadcastUrl, contentUrl, wasmFileUrl, websocketUrl}) {
    const {requestedLanguage} = resolveCurrentLanguage(ApplicationConfig.initParams.language);
    const locale = resolveContentLocale(requestedLanguage);
    const feedParams = getFeedParams({broadcastUrl, contentUrl, wasmFileUrl, websocketUrl});
    const swConfig = getServiceWorkerConfig(merge(feedParams, {locale: requestedLanguage}));

    if (swConfig) {
        try {
            await bootstrapVisionWorker(swConfig, {locale});
        } catch (err) {
            swConfig &&
                log.error('Failed to init vision with service worker. Falling back to inline vision setup', err);
            await bootstrapVisionInline(merge(feedParams, {locale}));
        }
    } else {
        await bootstrapVisionInline(merge(feedParams, {locale}));
    }
}
